import "./holidaysLimit.scss";
import React, { useState, useEffect } from "react";
import TableComponent from "../../../helpersComponents/TableComponent/tableComponent";
import { useTranslation } from "react-i18next";
import useFetchGet from "../../../../hooks/fetchHooks/get/useFetchGet";
import LoadingWrapper from "../../../helpersComponents/LoadingWrapper/loadingWrapper";
import { TopBanner } from "../../../helpersComponents/TopBanner/topBanner";
import { useAppSelector } from "../../../../store/hooks";
import { selectTimeWorkersSuperiorObject } from "../../../../reducers/workersLists";
import { selectSettings } from "../../../../reducers/settings";
import { AbsenceUnit } from "../../../../Constants/absenceUnit";
import { AbsenceType } from "../../../../Constants/absenceType";
import { bankersRound } from "../../../../HelpersFunctions/math/rounding/bankersRound";

type HolidaysLimit = {
  timeWorkerId: number;
  firstName: string;
  lastName: string;
  evidenceNumber: string;
  leaveDuration: number;
  oryginalLeaveUnit: number;
  overdueLeave: number;
  usedLeave: number;
  remainedLeave: number;
  remainedOverdue: number;
  educationalLeaveDuration: number;
  oryginalEducationalLeaveUnit: number;
  usedEducationalLeave: number;
  remainedEducationalLeave: number;
  leaveOnDemandDuration: number;
  oryginalLeaveOnDemandUnit: number;
  usedLeaveOnDemand: number;
  remainedLeaveOnDemand: number;
  careA188Duration: number;
  oryginalCareA188Unit: number;
  usedCareA188: number;
  remainedCareA188: number;
  leaveFromRequests: number;
};

function HolidaysLimitContent() {
  const { t } = useTranslation();
  const settings = useAppSelector(selectSettings);
  const [rows, setRows] = useState<object[]>([]);
  const columns = [
    { name: "employee", title: t("employee") },
    { name: "evidenceNumber", title: t("evidence_number") },
    { name: "organizationalUnits", title: t("organizational_units") },
    { name: "uwLimit", title: t("uw_limit") },
    { name: "uwOverdue", title: t("uw_overdue") },
    { name: "remainingUw", title: t("remaining_uw") },
    { name: "remainingOverdueUw", title: t("remaining_overdue_uw") },
    { name: "leaveOnDemand", title: t("leave_on_demand") },
    { name: "remainingNz", title: t("remaining_nz") },
    { name: "careLimit", title: t("care_limit") },
    { name: "remainingCare", title: t("remaining_care") },
  ];
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [ifFetchAgain, setIfFetchAgain] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  let holidayHoursLimitInDaysOption: boolean =
    settings.WebWymiarGodzinowyWDniach?.value === "1";

  const { data, error } = useFetchGet(
    "superior-time/holiday-limits",
    ifFetchAgain
  );

  const timeWorkersSuperiorObject = useAppSelector(
    selectTimeWorkersSuperiorObject
  );

  useEffect(() => {
    if (error !== null) {
      setIsError(true);
      setIfFetchAgain(false);
      return;
    }
    if (data === null) return;
    console.log(data);

    let id = 0;
    let dataModified: any = [];
    data.forEach((value: HolidaysLimit) => {
      dataModified.push({
        id: id,
        employee: value.firstName + " " + value.lastName,
        organizationalUnits:
          timeWorkersSuperiorObject[value.timeWorkerId]?.organizationalUnits,
        evidenceNumber:
          timeWorkersSuperiorObject[value.timeWorkerId]?.evidenceNumber,
        remainingCare: convertTime(
          value,
          AbsenceType.REMAINED_CARE_A188,
          holidayHoursLimitInDaysOption
        ),
        careLimit: convertTime(
          value,
          AbsenceType.CARE_A188_DURATION,
          holidayHoursLimitInDaysOption
        ),
        uwLimit: convertTime(
          value,
          AbsenceType.LEAVE_DURATION,
          holidayHoursLimitInDaysOption
        ),
        uwOverdue: convertTime(
          value,
          AbsenceType.OVERDUE_LEAVE,
          holidayHoursLimitInDaysOption
        ),
        remainingUw: convertTime(
          value,
          AbsenceType.REMAINED_LEAVE,
          holidayHoursLimitInDaysOption
        ),
        remainingOverdueUw: convertTime(
          value,
          AbsenceType.REMAINED_OVERDUE_LEAVE,
          holidayHoursLimitInDaysOption
        ),
        leaveOnDemand: convertTime(
          value,
          AbsenceType.LEAVE_ON_DEMAND_DURATION,
          holidayHoursLimitInDaysOption
        ),
        remainingNz: convertTime(
          value,
          AbsenceType.REMAINED_LEAVE_ON_DEMAND,
          holidayHoursLimitInDaysOption
        ),
      });
      id++;
    });
    setIfFetchAgain(false);
    setRows(dataModified);
    setIsLoading(false);
  }, [
    data,
    error,
    timeWorkersSuperiorObject,
    t,
    holidayHoursLimitInDaysOption,
  ]);

  return (
    <>
      <TopBanner pathName={t("holidays_limit")} />
      <div className="flexAndCenter">
        <div>
          <div>
            <LoadingWrapper
              isLodadingProgress={isLoading}
              isError={isError}
              setIfFetchAgain={setIfFetchAgain}
              setIsError={setIsError}
            >
              {!isLoading && !isError && (
                <TableComponent
                  title={t("holidays_limit_for_current_year")}
                  layoutSettingName="layoutSuperiorTimeAdministrationHolidatsLimit"
                  rows={rows}
                  columns={columns}
                  ifEdit={false}
                  ifAdd={false}
                  ifDelete={false}
                />
              )}
            </LoadingWrapper>
            {error ? error.title : null}
          </div>
        </div>
      </div>
    </>
  );
}

function convertTime(
  holidaysLimitObj: HolidaysLimit,
  absenceType: AbsenceType,
  holidayHoursLimitInDays: boolean
) {
  let oryginalAbsenceUnit: number = 0;
  let timeInHours: number;

  switch (absenceType) {
    case AbsenceType.LEAVE_DURATION:
      oryginalAbsenceUnit = holidaysLimitObj.oryginalLeaveUnit;
      timeInHours = holidaysLimitObj.leaveDuration;
      break;

    case AbsenceType.OVERDUE_LEAVE:
      oryginalAbsenceUnit = holidaysLimitObj.oryginalLeaveUnit;
      timeInHours = holidaysLimitObj.overdueLeave;
      break;

    case AbsenceType.REMAINED_LEAVE:
      oryginalAbsenceUnit = holidaysLimitObj.oryginalLeaveUnit;
      timeInHours = holidaysLimitObj.remainedLeave;
      break;

    case AbsenceType.REMAINED_OVERDUE_LEAVE:
      oryginalAbsenceUnit = holidaysLimitObj.oryginalLeaveUnit;
      timeInHours = holidaysLimitObj.remainedOverdue;
      break;

    case AbsenceType.LEAVE_ON_DEMAND_DURATION:
      oryginalAbsenceUnit = holidaysLimitObj.oryginalLeaveOnDemandUnit;
      timeInHours = holidaysLimitObj.leaveOnDemandDuration;
      break;

    case AbsenceType.REMAINED_LEAVE_ON_DEMAND:
      oryginalAbsenceUnit = holidaysLimitObj.oryginalLeaveOnDemandUnit;
      timeInHours = holidaysLimitObj.remainedLeaveOnDemand;
      break;

    case AbsenceType.CARE_A188_DURATION:
      oryginalAbsenceUnit = holidaysLimitObj.oryginalCareA188Unit;
      timeInHours = holidaysLimitObj.careA188Duration;
      break;

    case AbsenceType.REMAINED_CARE_A188:
      oryginalAbsenceUnit = holidaysLimitObj.oryginalCareA188Unit;
      timeInHours = holidaysLimitObj.remainedCareA188;
      break;
  }

  let timeToRet = timeInHours;

  switch (oryginalAbsenceUnit) {
    case AbsenceUnit.DAYS:
      timeToRet = timeInHours / 8;
      break;

    case AbsenceUnit.HOURS:
      timeToRet = timeInHours;
      break;
  }

  if (holidayHoursLimitInDays) {
    if (absenceType !== AbsenceType.REMAINED_CARE_A188) {
      if (holidaysLimitObj.oryginalLeaveUnit === AbsenceUnit.HOURS) {
        timeToRet = timeToRet / 8;
      }
    }
  }

  return bankersRound(timeToRet, 2);
}

export default HolidaysLimitContent;
